import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { logError } from '@/utils/errorHandler';

export type ConfirmationVariant = 'destructive' | 'warning' | 'info' | 'success';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmationVariant;
  isLoading?: boolean;
  children?: React.ReactNode;
  // Optional metadata to display
  metadata?: {
    label: string;
    value: string;
  }[];
  // Optional warning text
  warningText?: string;
}

const variantConfig = {
  destructive: {
    icon: XCircle,
    iconColor: 'text-red-500',
    confirmButtonVariant: 'destructive' as const,
    borderColor: 'border-red-200',
    bgColor: 'bg-red-50',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
    confirmButtonVariant: 'secondary' as const,
    borderColor: 'border-amber-200',
    bgColor: 'bg-amber-50',
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-500',
    confirmButtonVariant: 'secondary' as const,
    borderColor: 'border-blue-200',
    bgColor: 'bg-blue-50',
  },
  success: {
    icon: CheckCircle,
    iconColor: 'text-green-500',
    confirmButtonVariant: 'secondary' as const,
    borderColor: 'border-green-200',
    bgColor: 'bg-green-50',
  },
};

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning',
  isLoading = false,
  children,
  metadata,
  warningText,
}: ConfirmationModalProps) {
  const config = variantConfig[variant];
  const IconComponent = config.icon;

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      // Error handling is managed by the parent component
      logError(error as Error, {
        component: 'ConfirmationModal',
        action: 'handleConfirm',
        metadata: {
          title,
          variant,
          confirmText,
          hasMetadata: !!metadata,
          hasWarningText: !!warningText,
        },
      });
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader className="space-y-4">
          {/* Icon and Title */}
          <div className="flex items-start space-x-4">
            <div
              className={cn(
                'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center',
                config.bgColor,
                config.borderColor,
                'border-2'
              )}
            >
              <IconComponent className={cn('w-6 h-6', config.iconColor)} />
            </div>
            <div className="min-w-0 flex-1">
              <AlertDialogTitle className="text-lg font-semibold leading-6 text-gray-900">
                {title}
              </AlertDialogTitle>
            </div>
          </div>

          {/* Description */}
          <AlertDialogDescription className="text-sm leading-relaxed text-gray-600">
            {description}
          </AlertDialogDescription>

          {/* Metadata */}
          {metadata && metadata.length > 0 && (
            <div className="space-y-2 rounded-lg bg-gray-50 p-4">
              {metadata.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{item.label}:</span>
                  <Badge variant="outline" className="font-mono text-xs">
                    {item.value}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          {/* Warning Text */}
          {warningText && (
            <div
              className={cn(
                'rounded-lg p-3 border-l-4',
                variant === 'destructive'
                  ? 'bg-red-50 border-red-400'
                  : 'bg-amber-50 border-amber-400'
              )}
            >
              <div className="flex items-start">
                <AlertTriangle
                  className={cn(
                    'w-4 h-4 mt-0.5 mr-2 flex-shrink-0',
                    variant === 'destructive' ? 'text-red-500' : 'text-amber-500'
                  )}
                />
                <p
                  className={cn(
                    'text-sm font-medium',
                    variant === 'destructive' ? 'text-red-800' : 'text-amber-800'
                  )}
                >
                  {warningText}
                </p>
              </div>
            </div>
          )}

          {/* Custom children content */}
          {children && <div className="pt-2">{children}</div>}
        </AlertDialogHeader>

        <AlertDialogFooter className="flex flex-col-reverse space-y-2 space-y-reverse sm:flex-row sm:justify-end sm:space-x-2 sm:space-y-0">
          <AlertDialogCancel asChild>
            <Button variant="outline" disabled={isLoading} className="w-full sm:w-auto">
              {cancelText}
            </Button>
          </AlertDialogCancel>

          <AlertDialogAction asChild>
            <Button
              variant={config.confirmButtonVariant}
              onClick={handleConfirm}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Processing...
                </>
              ) : (
                confirmText
              )}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Hook for easier usage
export function useConfirmationModal() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    if (!isLoading) {
      setIsOpen(false);
    }
  };

  const handleConfirm = async (action: () => void | Promise<void>) => {
    setIsLoading(true);
    try {
      await action();
      setIsOpen(false);
    } catch (error) {
      // Keep modal open on error
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isOpen,
    isLoading,
    openModal,
    closeModal,
    handleConfirm,
  };
}
